import { type FormEvent, useEffect, useState } from 'react'
import { resetUserPassword } from '~/api/users'
import { Button } from '~/components/ui/button'
import { Card } from '~/components/ui/card'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { useToast } from '~/components/ui/toast'

// superadmin 비밀번호 재설정 모달. 이메일 복구가 없어 콘솔에서 임시 비밀번호를 직접 설정한다.
// 새/새 확인 2필드. 일치·8자 검증은 클라이언트에서 막고, 정책 위반(400)은 서버 message를 토스트로 보여준다.
// 비밀번호 평문은 서버 전송에만 쓰고 화면·로그 어디에도 남기지 않는다.

const MIN_LENGTH = 8

export function ResetPasswordDialog({
    userId,
    email,
    onClose,
    onDone,
}: {
    // userId가 있으면 열린 상태. 없으면 렌더하지 않는다.
    userId: string | null
    email: string
    onClose: () => void
    onDone?: () => void
}) {
    const toast = useToast()
    const [next, setNext] = useState('')
    const [confirm, setConfirm] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)

    // 닫힐 때 입력·상태를 비운다(다음 열람 시 잔상·평문 잔류 방지).
    useEffect(() => {
        if (userId) return
        setNext('')
        setConfirm('')
        setError(null)
        setBusy(false)
    }, [userId])

    // 열려 있는 동안 Esc로 닫는다.
    useEffect(() => {
        if (!userId) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [userId, onClose])

    if (!userId) return null

    const submit = async (e: FormEvent) => {
        e.preventDefault()
        if (next.length < MIN_LENGTH) {
            setError(`새 비밀번호는 ${MIN_LENGTH}자 이상이어야 해요.`)
            return
        }
        if (next !== confirm) {
            setError('새 비밀번호가 서로 일치하지 않아요.')
            return
        }
        setError(null)
        setBusy(true)
        try {
            await resetUserPassword(userId, next)
            toast.success(
                '비밀번호를 재설정했어요 — 사용자에게 임시 비밀번호를 직접 전달하세요.'
            )
            onDone?.()
            onClose()
        } catch (err) {
            toast.error((err as Error).message)
        } finally {
            setBusy(false)
        }
    }

    return (
        <div
            className='fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4'
            onClick={onClose}>
            <Card
                role='dialog'
                aria-modal='true'
                aria-labelledby='reset-password-title'
                className='w-full max-w-sm p-5 shadow-sm'
                onClick={(e) => e.stopPropagation()}>
                <h2
                    id='reset-password-title'
                    className='text-foreground text-base font-semibold tracking-tight'>
                    비밀번호 재설정
                </h2>
                <p className='text-muted-foreground mt-1 text-[13px]'>
                    {email} 사용자의 새 임시 비밀번호를 설정해요. 설정 후 직접
                    전달하세요.
                </p>

                <form onSubmit={submit} className='mt-4 space-y-3'>
                    <div className='space-y-1.5'>
                        <Label htmlFor='rp-next'>새 비밀번호</Label>
                        <Input
                            id='rp-next'
                            type='password'
                            autoComplete='new-password'
                            value={next}
                            onChange={(e) => setNext(e.target.value)}
                            error={!!error}
                            autoFocus
                            required
                        />
                    </div>
                    <div className='space-y-1.5'>
                        <Label htmlFor='rp-confirm'>새 비밀번호 확인</Label>
                        <Input
                            id='rp-confirm'
                            type='password'
                            autoComplete='new-password'
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            error={!!error}
                            required
                        />
                    </div>
                    {error ? (
                        <p className='text-destructive text-[12px]'>{error}</p>
                    ) : (
                        <p className='text-muted-foreground text-[12px]'>
                            새 비밀번호는 {MIN_LENGTH}자 이상으로 설정해 주세요.
                        </p>
                    )}

                    <div className='flex justify-end gap-2 pt-1'>
                        <Button
                            type='button'
                            variant='outline'
                            size='sm'
                            onClick={onClose}>
                            취소
                        </Button>
                        <Button type='submit' size='sm' disabled={busy}>
                            {busy ? '재설정하는 중…' : '비밀번호 재설정'}
                        </Button>
                    </div>
                </form>
            </Card>
        </div>
    )
}
